import errorMessages from "../../translations/en/errors.json";

/**
 * A user-facing reason the app is showing the login page instead of the app itself.
 *
 * Auth failures that happen while the user is away at an identity provider (or in a background
 * refresh) have no form submission to report back to, so they are surfaced through AuthContext
 * and rendered by the login form instead of being swallowed by a bare redirect to /login.
 */
export interface AuthError {
  /** Stable machine code for logging/support. Never rendered. */
  code: string;
  /** Friendly, actionable sentence shown to the user. */
  message: string;
  /** Raw provider/API detail, shown as secondary text so support can diagnose real failures. */
  details?: string;
  /** Email the failed attempt was for, used to pre-fill the login form. */
  email?: string;
}

const sso = errorMessages.sso;
const sessionMessages = errorMessages.session;

const MAX_DETAILS_LENGTH = 200;

/**
 * Providers return free-form text (often URL-encoded, occasionally very long). Keep it short,
 * single-line and printable so it can never blow up the login layout.
 */
function sanitizeDetails(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const text = value.replace(/\+/g, " ").replace(/\s+/g, " ").trim();
  if (!text) return undefined;
  return text.length > MAX_DETAILS_LENGTH ? `${text.slice(0, MAX_DETAILS_LENGTH)}...` : text;
}

export function isAuthError(value: unknown): value is AuthError {
  const candidate = value as AuthError | undefined;
  return Boolean(candidate && typeof candidate.code === "string" && typeof candidate.message === "string");
}

/**
 * True when the backend answered with the case-sensitivity migration's "neither the lowercase
 * nor the exact-typed-case Cognito account exists" signal (login/password-reset/resend-code) --
 * the caller should redirect to /register with the message rather than showing a raw error.
 */
export function isAccountNotFoundError(error: any): boolean {
  return error?.response?.status === 404 && error?.response?.data?.code === "ACCOUNT_NOT_FOUND";
}

/**
 * Register/resetPassword/verifyResetPassword/resendSignUpCode used to call Amplify directly,
 * whose rejected errors carry the real failure text on `.message` -- forms across the app catch
 * these with a plain `setError(err.message)`. Now that those same calls go through the backend
 * via axios, a rejected request's `.message` is a generic "Request failed with status code..."
 * instead. Mutating `.message` in place (rather than throwing a new Error) keeps `.response`
 * intact, so callers like `isAccountNotFoundError` above still work on the same error object.
 */
export function withServerErrorMessage(err: any): never {
  const apiMessage = err?.response?.data?.message;
  if (typeof apiMessage === "string" && apiMessage) {
    err.message = apiMessage;
  }
  throw err;
}

/** The state we handed the provider did not come back intact (stale tab, replayed callback). */
export const SSO_STATE_MISMATCH_ERROR: AuthError = { code: "SSO_STATE_MISMATCH", message: sso.stateMismatch };

/** A provider callback arrived that this app has no way to complete (no state, no IdP client). */
export const SSO_UNSUPPORTED_CALLBACK_ERROR: AuthError = { code: "SSO_UNSUPPORTED_CALLBACK", message: sso.misconfigured };

/** The provider answered, but the app could not turn that answer into a session. */
export const SSO_EXCHANGE_FAILED_ERROR: AuthError = { code: "SSO_EXCHANGE_FAILED", message: sso.exchangeFailed };

/** An existing session could not be refreshed, so it was dropped. */
export const SESSION_EXPIRED_ERROR: AuthError = { code: "SESSION_EXPIRED", message: sessionMessages.expired };

/**
 * The identity provider (or Cognito itself) bounced the user back to the app with `?error=`
 * instead of `?code=` -- the OAuth2 error response of RFC 6749 4.1.2.1, plus the OIDC additions.
 * Without this the app just sees "no code" and silently falls through to the login page.
 */
export function describeOAuthCallbackError(error: string, description?: string): AuthError {
  const details = sanitizeDetails(description);

  switch ((error || "").toLowerCase()) {
    case "access_denied":
      return { code: "SSO_ACCESS_DENIED", message: sso.accessDenied, details };
    case "unauthorized_client":
    case "invalid_client":
    case "unauthorized":
      return { code: "SSO_NOT_AUTHORIZED", message: sso.notAuthorized, details };
    case "server_error":
    case "temporarily_unavailable":
      return { code: "SSO_PROVIDER_UNAVAILABLE", message: sso.providerUnavailable, details };
    case "invalid_request":
    case "invalid_scope":
    case "unsupported_response_type":
      return { code: "SSO_MISCONFIGURED", message: sso.misconfigured, details };
    case "login_required":
    case "interaction_required":
    case "consent_required":
      return { code: "SSO_INTERACTION_REQUIRED", message: sso.interactionRequired, details };
    default:
      return { code: "SSO_FAILED", message: sso.generic, details: details ?? sanitizeDetails(error) };
  }
}

/**
 * Failure of POST auth/sso/exchange -- the app trading the provider's authorization code for a
 * session. The API returns { code, message } for the cases it can explain (see SsoService).
 */
export function describeSsoExchangeError(error: any): AuthError {
  if (!error?.response) {
    // axios attaches `request` once it went out -- no response after that means the network or
    // the server dropped it. Anything else never made it out of the browser.
    return error?.request
      ? { code: "SSO_NETWORK", message: sso.network }
      : { code: "SSO_EXCHANGE_FAILED", message: sso.exchangeFailed };
  }

  const { status, data } = error.response;
  const apiCode: string | undefined = typeof data?.code === "string" ? data.code : undefined;
  const apiMessage: string | undefined = typeof data?.message === "string" ? data.message : undefined;

  // Already worded for the end user by the API -- pass it through verbatim.
  if (apiCode === "SSO_EMAIL_MISMATCH") {
    return { code: apiCode, message: apiMessage || sso.emailMismatch };
  }

  if (status === 401 || status === 403) {
    return { code: apiCode || "SSO_NOT_AUTHORIZED", message: apiMessage || sso.notAuthorized };
  }

  if (status === 404) {
    return { code: apiCode || "SSO_PROVIDER_DISABLED", message: sso.providerDisabled };
  }

  if (status === 502 || status === 503 || status === 504) {
    // The API reached us but could not reach/parse the provider -- its message carries the
    // provider's own error text, which is the only useful diagnostic here.
    return { code: apiCode || "SSO_EXCHANGE_FAILED", message: sso.exchangeFailed, details: sanitizeDetails(apiMessage) };
  }

  if (status >= 500) {
    return { code: "SSO_SERVER_ERROR", message: sso.serverError };
  }

  return { code: apiCode || "SSO_EXCHANGE_FAILED", message: sso.exchangeFailed, details: sanitizeDetails(apiMessage) };
}

/** Failure of POST auth/sso/initiate -- before the user ever leaves the app. */
export function describeSsoInitiateError(error: any): AuthError {
  if (!error?.response) {
    return error?.request
      ? { code: "SSO_NETWORK", message: sso.network }
      : { code: "SSO_INITIATE_FAILED", message: sso.initiateFailed };
  }

  const { status, data } = error.response;
  const apiCode: string | undefined = typeof data?.code === "string" ? data.code : undefined;

  if (status === 404) {
    return { code: apiCode || "SSO_PROVIDER_DISABLED", message: sso.providerDisabled };
  }

  if (status >= 500) {
    return { code: apiCode || "SSO_INITIATE_FAILED", message: sso.serverError };
  }

  return { code: apiCode || "SSO_INITIATE_FAILED", message: sso.initiateFailed };
}

/**
 * A silent token refresh failed, so the session was dropped and the user is about to be bounced
 * to the login page. A refused refresh really is a dead session; a refresh that never got an
 * answer, or that the API itself reports as a transient upstream failure (LOGIN_RETRY, e.g. a
 * Cognito blip), is a connectivity problem and must not be reported as an expiry.
 */
export function describeRefreshError(error: any): AuthError {
  if (!error?.response) {
    return { code: "SESSION_CONNECTION_LOST", message: sessionMessages.connectionLost };
  }

  if (error.response.data?.code === "LOGIN_RETRY") {
    return { code: "SESSION_CONNECTION_LOST", message: sessionMessages.connectionLost };
  }

  return SESSION_EXPIRED_ERROR;
}

/**
 * The account loaded past sign-in but the app could not fetch the user/org behind it, which
 * forces a sign-out. 401/403 means access was revoked or never granted; anything else is a
 * transient failure and must not be reported as a permissions problem.
 */
export function describeAccountLoadError(error: any): AuthError {
  // The http client normalises a failed request to { status, message }. A rejection that still
  // carries the raw axios response instead means the token refresh behind a 401 failed -- the
  // session died, which is not the same thing as the account losing access.
  if (error?.response && typeof error?.status !== "number") {
    return SESSION_EXPIRED_ERROR;
  }

  if (error?.status === 401 || error?.status === 403) {
    return { code: "ACCOUNT_ACCESS_REVOKED", message: sessionMessages.accessRevoked };
  }

  return { code: "ACCOUNT_LOAD_FAILED", message: sessionMessages.accountLoadFailed };
}
